import { AHIcon, AHIconType, AHTag, AHText, Box } from "arkh";
import React from "react";
import { graphql, useFragment } from "react-relay";
import { prioritizeEmployees } from "../TodoListItem/TodoListItemFooter/OverlappingEmployeeAvatars";
import { WidgetTodoListItemDeadline } from "./TodoListItemComponents/WidgetTodoListItemDeadline";
import { TodoOverviewListItemMediumFooter_Todo$key } from "./__generated__/TodoOverviewListItemMediumFooter_Todo.graphql";

export const TodoOverviewListItemMediumFooter = React.memo(
  TodoOverviewListItemMediumFooter_
);
function TodoOverviewListItemMediumFooter_(props: {
  todo: TodoOverviewListItemMediumFooter_Todo$key;
}) {
  const todo = useFragment(
    graphql`
      fragment TodoOverviewListItemMediumFooter_Todo on Todo {
        tags {
          id
          color
          title
        }
        ...WidgetTodoListItemDeadline_todo
        expireAt
        assignedRoles {
          role {
            name
            employees {
              id
              name
              isLinkedToViewer
              workStatus
            }
          }
        }
        subTodos {
          status
          __typename
        }
        assigned {
          employee {
            name
          }
        }
        commentCount
      }
    `,
    props.todo
  );

  const names = [
    ...todo.assigned.map((e) => e.employee?.name).filter((n) => n),
  ];

  const assigneeText = names
    .map((name) => {
      if (!name) {
        return "";
      }
      const parts = name.split(" ");
      return `${parts[0]}${parts.length > 1 ? " " + parts[1].charAt(0) : ""}`;
    })
    .join(" · ");

  const rolesText = todo.assignedRoles
    .map((r) => {
      if (!r.role?.name) return null;
      const employeeNames = prioritizeEmployees(r.role.employees ?? [], undefined)
        .map((e) => {
          if (!e.name) return "";
          const parts = e.name.split(" ");
          return `${parts[0]}${parts.length > 1 ? " " + parts[1].charAt(0) : ""}`;
        })
        .filter((n) => n);
      if (employeeNames.length > 0) {
        return `${r.role.name}: ${employeeNames.join(" · ")}`;
      }
      return r.role.name;
    })
    .filter((n) => n)
    .join(", ");

  if (
    !(
      todo.commentCount > 0 ||
      todo.expireAt ||
      todo.tags.length > 0 ||
      assigneeText.length > 0 ||
      rolesText.length > 0
    )
  ) {
    return null;
  }
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",

        flexShrink: 1,
        justifyContent: "flex-start",
      }}
    >
      {todo.expireAt ? (
        <Box spaceRight="small">
          <WidgetTodoListItemDeadline todo={todo} />
        </Box>
      ) : null}
      {assigneeText.length > 0 ? (
        <FooterItem icon="users">{assigneeText}</FooterItem>
      ) : null}
      {rolesText.length > 0 ? <FooterItem icon="employee-role">{rolesText}</FooterItem> : null}
      <Box
        style={{
          flexDirection: "row",
          alignItems: "center",
          flexShrink: 1,
          justifyContent: "flex-start",
        }}
        spaceRight="small"
      >
        {todo.tags.map((tag, i) => (
          <Box
            key={tag.id}
            style={{
              flexShrink: tag.title.length,
            }}
            spaceLeft={i === 0 ? "none" : "xsmall"}
          >
            <AHTag
              color={tag.color}
              type="outside"
              title={tag.title}
              size="small"
            />
          </Box>
        ))}
      </Box>
      {todo.commentCount > 0 ? (
        <FooterItem icon="message">{todo.commentCount}</FooterItem>
      ) : null}
    </Box>
  );
}

function FooterItem(props: { children: React.ReactNode; icon: AHIconType }) {
  return (
    <Box
      style={{
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 1,
        justifyContent: "flex-start",
      }}
    >
      <AHIcon
        type={props.icon}
        size="xxsmall"
        color="washedText"
        spaceRight="xsmall"
      />
      <AHText
        color="washedText"
        type="detail"
        numberOfLines={1}
        spaceRight="small"
      >
        {props.children}
      </AHText>
    </Box>
  );
}
